import { useState, useEffect, useRef } from "react";
import { ImageSquare, PencilSimple, Plus, X, Trash, UploadSimple, TextT } from "@phosphor-icons/react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBanner() {
  const { admin } = useAdminAuth();
  const canDelete = admin?.allow_delete !== false;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const fileRef = useRef();
  const [form, setForm] = useState({ chip_title: "", file: null });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const fetchItems = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/banners`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
  };

  useEffect(() => { fetchItems().finally(() => setLoading(false)); }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((f) => ({ ...f, file }));
    setPreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setForm({ chip_title: "", file: null });
    setPreview(null);
    setError("");
    setEditingItem(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setForm({ chip_title: item.chip_title || "", file: null });
    setPreview(`${BACKEND_URL}${item.image_url}`);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
    setDrawerOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem && !form.file) { setError("Please upload an image."); return; }
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("chip_title", form.chip_title.trim());
      if (form.file) fd.append("file", form.file);
      const res = await fetch(
        editingItem ? `${BACKEND_URL}/api/banners/${editingItem.id}` : `${BACKEND_URL}/api/banners`,
        { method: editingItem ? "PUT" : "POST", body: fd }
      );
      if (!res.ok) throw new Error("Upload failed");
      await fetchItems();
      resetForm();
      setDrawerOpen(false);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
    try {
      await fetch(`${BACKEND_URL}/api/banners/${id}`, { method: "DELETE" });
      setItems((prev) => prev.filter((x) => x.id !== id));
    } finally {
      setDeleteId(null);
    }
  };

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border text-sm outline-none transition-colors";
  const inputStyle = { borderColor: "rgba(26,92,55,0.2)", backgroundColor: "#F7F5F0", color: "#1C211F" };
  const labelCls = "block text-xs font-medium mb-1.5 uppercase tracking-wider";

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>Homepage Banners</h3>
          <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>
            {items.length} banner{items.length !== 1 ? "s" : ""} · scrolls every 3s on the homepage
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setDrawerOpen(true); }}
          data-testid="add-banner-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
        >
          <Plus size={16} weight="bold" />
          Add Banner
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
            <ImageSquare size={28} weight="duotone" style={{ color: "#1A5C37" }} />
          </div>
          <p className="text-sm font-medium" style={{ color: "#1C211F" }}>No banners yet</p>
          <p className="text-xs mt-1" style={{ color: "#4A5450" }}>Add a banner to display it on the homepage hero.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((b) => (
            <div key={b.id} className="relative rounded-xl overflow-hidden border group" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
              <div className="relative aspect-[16/9]">
                <img src={`${BACKEND_URL}${b.image_url}`} alt={b.chip_title || "Homepage banner"} className="w-full h-full object-cover" />
                {b.chip_title && (
                  <span
                    className="absolute bottom-2 right-2 px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{ backgroundColor: "rgba(247,245,240,0.92)", color: "#1A5C37" }}
                  >
                    {b.chip_title}
                  </span>
                )}
                {canDelete && (
                  <button
                    onClick={() => handleDelete(b.id)}
                    disabled={deleteId === b.id}
                    data-testid={`delete-banner-${b.id}`}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: "rgba(184,92,56,0.92)", color: "#fff" }}
                  >
                    <Trash size={14} weight="duotone" />
                  </button>
                )}
                <button
                  onClick={() => startEdit(b)}
                  className="absolute top-2 left-2 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: "rgba(26,92,55,0.92)", color: "#fff" }}
                >
                  <PencilSimple size={14} weight="duotone" />
                </button>
              </div>
              <div className="px-3 py-2" style={{ backgroundColor: "#fff" }}>
                <p className="text-xs font-medium truncate" style={{ color: "#1C211F" }}>{b.chip_title || "Untitled banner"}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40" style={{ backgroundColor: "rgba(0,0,0,0.35)" }} onClick={() => setDrawerOpen(false)} />
      )}
      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ width: "min(420px, 100vw)", backgroundColor: "#fff", boxShadow: "-8px 0 32px rgba(0,0,0,0.12)" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <h3 className="font-heading text-lg font-medium" style={{ color: "#1C211F" }}>{editingItem ? "Edit Banner" : "Add Banner"}</h3>
          <button onClick={() => setDrawerOpen(false)} className="p-1.5 rounded-lg" style={{ color: "#4A5450" }}>
            <X size={20} weight="bold" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Upload Image</label>
            <div
              className="relative flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed cursor-pointer"
              style={{ borderColor: preview ? "#1A5C37" : "rgba(26,92,55,0.25)", backgroundColor: "#F7F5F0", minHeight: 160 }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              {preview ? (
                <img src={preview} alt="preview" className="max-h-36 rounded-lg object-cover" />
              ) : (
                <>
                  <UploadSimple size={28} weight="duotone" style={{ color: "#1A5C37" }} />
                  <p className="text-sm" style={{ color: "#4A5450" }}>{editingItem ? "Click to replace banner image" : "Click to upload banner image"}</p>
                </>
              )}
              {preview && (
                <button
                  type="button"
                  className="absolute top-2 right-2 p-1 rounded-full"
                  style={{ backgroundColor: "rgba(184,92,56,0.1)", color: "#B85C38" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreview(editingItem ? `${BACKEND_URL}${editingItem.image_url}` : null);
                    setForm((f) => ({ ...f, file: null }));
                    if (fileRef.current) fileRef.current.value = "";
                  }}
                >
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Chip Title Optional</label>
            <div className="relative">
              <TextT size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#7A8A84" }} />
              <input
                type="text"
                placeholder="e.g. Before, After, Restoration"
                value={form.chip_title}
                onChange={(e) => setForm((f) => ({ ...f, chip_title: e.target.value }))}
                className={inputCls}
                style={{ ...inputStyle, paddingLeft: "2.25rem" }}
                data-testid="banner-chip-title-input"
              />
            </div>
          </div>

          {error && <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}>{error}</p>}
        </form>
        <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <button type="button" onClick={() => setDrawerOpen(false)} className="flex-1 py-2.5 rounded-lg text-sm font-medium border" style={{ borderColor: "rgba(26,92,55,0.2)", color: "#4A5450" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting} className="flex-1 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: submitting ? "#4a8c62" : "#1A5C37", color: "#F7F5F0" }} data-testid="banner-submit-btn">
            {submitting ? "Saving..." : editingItem ? "Save Banner" : "Add Banner"}
          </button>
        </div>
      </div>
    </div>
  );
}
